/**
 * Compliance tab on the visit worklist (2.8.1): SOC-by / ROC-by, recert window,
 * supervisory due, frequency shortfall, visits at risk, no reply, availability
 * conflict, auth due, canceled visits to rebook, unfilled visits, contested
 * placements and PTO remainders — as countdowns, severity-sorted, filterable by
 * patient, clinician, discipline and territory. Act 5.3 reads it branch-wide and
 * then narrows it to one territory; the rows themselves are the same rows.
 */
import { useMemo, useState } from "react";
import { Box, Chip, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useNow } from "../../components/useNow";
import { territoryNames } from "@caredna-marlin/utils/homeHealthDemo/engine/territory";
import { KIND_LABELS, selectWorklistRows, type WorklistKind } from "../../engine/worklist";
import type { Discipline } from "@caredna-marlin/utils/homeHealthDemo/state";
import { useDemoState } from "../../store/useDemoStore";
import { SeverityDot, WorklistRows } from "./WorklistRows";

const DISCIPLINES: Discipline[] = ["RN", "LPN", "PT", "PTA", "OT", "COTA", "SLP", "MSW", "HHA"];

export function ComplianceTab() {
  const state = useDemoState();
  const now = useNow();
  const [patientRef, setPatientRef] = useState("");
  const [clinicianHandle, setClinicianHandle] = useState("");
  const [discipline, setDiscipline] = useState("");
  const [territory, setTerritory] = useState("");
  const [kinds, setKinds] = useState<WorklistKind[]>([]);
  const all = useMemo(() => selectWorklistRows(state, now), [state, now]);
  const rows = useMemo(
    () => selectWorklistRows(state, now, { patientRef: patientRef || undefined, clinicianHandle: clinicianHandle || undefined, discipline: discipline || undefined, territory: territory || undefined, kinds: kinds.length ? kinds : undefined }),
    [state, now, patientRef, clinicianHandle, discipline, territory, kinds],
  );
  const presentKinds = Array.from(new Set(all.map((r) => r.kind)));
  const patients = Array.from(new Set(all.map((r) => r.patientRef).filter(Boolean))) as string[];
  const clinicians = Array.from(new Set(all.map((r) => r.clinicianHandle).filter(Boolean))) as string[];
  const territories = territoryNames(state.config.branch);
  const toggleKind = (k: WorklistKind) => setKinds((ks) => (ks.includes(k) ? ks.filter((x) => x !== k) : [...ks, k]));

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Compliance
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {rows.length} of {all.length}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ ml: "auto" }} alignItems="center">
          <SeverityDot severity={3} />
          <Typography variant="caption">now</Typography>
          <SeverityDot severity={2} />
          <Typography variant="caption">today</Typography>
          <SeverityDot severity={1} />
          <Typography variant="caption">this week</Typography>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <TextField size="small" select label="Patient" value={patientRef} onChange={(e) => setPatientRef(e.target.value)} sx={{ minWidth: 170 }}>
          <MenuItem value="">All patients</MenuItem>
          {patients.map((p) => (
            <MenuItem key={p} value={p}>
              {state.patients[p]?.displayName ?? p}
            </MenuItem>
          ))}
        </TextField>
        <TextField size="small" select label="Clinician" value={clinicianHandle} onChange={(e) => setClinicianHandle(e.target.value)} sx={{ minWidth: 170 }}>
          <MenuItem value="">All clinicians</MenuItem>
          {clinicians.map((h) => (
            <MenuItem key={h} value={h}>
              {state.roster[h]?.displayName ?? h}
            </MenuItem>
          ))}
        </TextField>
        <TextField size="small" select label="Discipline" value={discipline} onChange={(e) => setDiscipline(e.target.value)} sx={{ minWidth: 130 }}>
          <MenuItem value="">All disciplines</MenuItem>
          {DISCIPLINES.map((d) => (
            <MenuItem key={d} value={d}>
              {d}
            </MenuItem>
          ))}
        </TextField>
        <TextField size="small" select label="Territory" value={territory} onChange={(e) => setTerritory(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="">All territories</MenuItem>
          {territories.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </TextField>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {presentKinds.map((k) => (
            <Chip key={k} size="small" label={`${KIND_LABELS[k]} · ${all.filter((r) => r.kind === k).length}`} color={kinds.includes(k) ? "primary" : "default"} variant={kinds.includes(k) ? "filled" : "outlined"} onClick={() => toggleKind(k)} />
          ))}
        </Stack>
      </Stack>

      <WorklistRows rows={rows} />
    </Box>
  );
}
